import { useEffect, useState } from 'react';

import { Alert, Button, Group, Modal, Select, Stack, Text, Textarea } from '@mantine/core';

import {
  CANCELLATION_REASON_LABELS,
  MonitoringCancellationReason,
  SELECTABLE_CANCELLATION_REASONS,
} from '@/types/monitoring';

// Module-level so the Select gets the same array every render.
const REASON_OPTIONS = SELECTABLE_CANCELLATION_REASONS.map((value) => ({
  value,
  label: CANCELLATION_REASON_LABELS[value],
}));

interface CancelMonitoredSubjectModalProps {
  opened: boolean;
  onClose: () => void;
  subjectName: string;
  /** Throws on failure; the modal stays open and shows the message. */
  onConfirm: (payload: {
    reason: MonitoringCancellationReason;
    note: string | null;
  }) => Promise<void>;
}

/**
 * Stop monitoring one subject, with a reason.
 *
 * The reason is a closed list rather than free text so cancellations can be
 * counted and compared later. `Other` needs a note, which the server enforces
 * too — a closed list with an unexplained escape hatch turns back into free
 * text.
 */
export function CancelMonitoredSubjectModal({
  opened,
  onClose,
  subjectName,
  onConfirm,
}: Readonly<CancelMonitoredSubjectModalProps>) {
  const [reason, setReason] = useState<MonitoringCancellationReason | null>(null);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cleared on close, so reopening for another subject never inherits a reason.
  useEffect(() => {
    if (!opened) {
      setReason(null);
      setNote('');
      setError(null);
    }
  }, [opened]);

  const noteRequired = reason === 'other';
  const canSubmit = reason !== null && (!noteRequired || note.trim() !== '') && !submitting;

  const submit = async () => {
    if (!reason) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm({ reason, note: note.trim() || null });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not cancel monitoring');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title={`Cancel monitoring — ${subjectName}`}>
      <Stack>
        <Text size="sm">
          The subject stops being watched and is removed from Castellum, which stops billing for it.
          No further alerts about it reach the client. This cannot be undone — monitoring it again
          means enrolling it again.
        </Text>
        <Select
          label="Reason"
          placeholder="Choose a reason"
          data={REASON_OPTIONS}
          value={reason}
          onChange={(value) => setReason(value as MonitoringCancellationReason | null)}
          allowDeselect={false}
          comboboxProps={{ withinPortal: false }}
        />
        <Textarea
          label="Note"
          description={noteRequired ? 'Required when the reason is Other' : 'Optional'}
          required={noteRequired}
          autosize
          minRows={2}
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
        />
        {error && (
          <Alert color="red" title="Nothing was changed">
            {error}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Keep monitoring
          </Button>
          <Button
            color="red"
            onClick={() => void submit()}
            disabled={!canSubmit}
            loading={submitting}
          >
            Cancel monitoring
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
